import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Tooltip,
  useTheme,
  Paper
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import api from '../utils/api';
import currencyService from '../services/currencyService';

// Mapping pays -> drapeau
const countryFlags = {
  FR: 'https://flagcdn.com/16x12/fr.png',
  ES: 'https://flagcdn.com/16x12/es.png',
  IT: 'https://flagcdn.com/16x12/it.png',
  DE: 'https://flagcdn.com/16x12/de.png',
  UK: 'https://flagcdn.com/16x12/gb.png',
  NL: 'https://flagcdn.com/16x12/nl.png',
  PL: 'https://flagcdn.com/16x12/pl.png',
  SE: 'https://flagcdn.com/16x12/se.png',
  BE: 'https://flagcdn.com/16x12/be.png',
  IE: 'https://flagcdn.com/16x12/ie.png',
};

// Mapping marketplace -> devise
const marketplaceCurrencies = {
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  DE: 'EUR',
  UK: 'GBP',
  NL: 'EUR',
  PL: 'PLN',
  SE: 'SEK',
  BE: 'EUR',
  IE: 'EUR'
};

// Mapping devise -> symbole
const currencySymbols = {
  EUR: '€',
  GBP: '£',
  PLN: 'zł',
  SEK: 'kr'
};

const formatVariation = (variation) => {
  if (variation === null || variation === undefined) return '-';
  return `${variation > 0 ? '+' : ''}${Number(variation).toFixed(2)} €`;
};

const formatPercent = (percent) => {
  if (percent === null || percent === undefined) return '-';
  return `${percent > 0 ? '+' : ''}${Number(percent).toFixed(2)}%`;
};

const getTypeIcon = (type) => {
  if (type === 'increase') {
    return <TrendingUp color="success" fontSize="small" />;
  }
  if (type === 'decrease') {
    return <TrendingDown color="error" fontSize="small" />;
  }
  return null;
};

const cellSx = {
  fontSize: '1em',
  maxWidth: 120,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  p: 0.5
};

const headerSx = {
  background: '#f6f8fa',
  fontWeight: 700,
  fontSize: '1em',
  color: '#223',
  borderBottom: '1.5px solid #e0e0e0',
  p: 0.5
};

const RepricingHistoryCard = () => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const theme = useTheme();

  // Affiche toujours le prix en euro (conversion si nécessaire) et un tooltip avec la valeur d'origine
  const formatPrice = (price, marketplace) => {
    if (!price) return '-';
    const currency = marketplaceCurrencies[marketplace];
    const symbol = currencySymbols[currency];
    let priceEur = price;
    console.log('Affichage prix:', { price, currency, taux: exchangeRates[currency], exchangeRates });
    if (currency !== 'EUR' && exchangeRates[currency]) {
      priceEur = price * exchangeRates[currency];
    }
    // Tooltip toujours affiché, même pour EUR
    return (
      <Tooltip title={`${price.toFixed(2)} ${symbol}`} arrow>
        <span>{priceEur.toFixed(2)} €</span>
      </Tooltip>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [historyData] = await Promise.all([
          api.getLatestRepricingHistory()
        ]);
        // Récupère les taux pour SEK, PLN, GBP
        const rates = {};
        for (const currency of ['SEK', 'PLN', 'GBP']) {
          try {
            rates[currency] = await currencyService.getExchangeRate(currency, 'EUR');
          } catch {
            rates[currency] = null;
          }
        }
        console.log('Taux de change récupérés:', rates);
        setHistory(historyData);
        setExchangeRates(rates);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 0 }}>
          Derniers changements de prix
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerSx, minWidth: 90 }}>Date</TableCell>
                <TableCell sx={{ ...headerSx, minWidth: 90 }}>Produit</TableCell>
                <TableCell sx={{ ...headerSx, minWidth: 60 }}>Marketplace</TableCell>
                <TableCell align="right" sx={{ ...headerSx, minWidth: 60 }}>Ancien</TableCell>
                <TableCell align="right" sx={{ ...headerSx, minWidth: 60 }}>Nouveau</TableCell>
                <TableCell align="right" sx={{ ...headerSx, minWidth: 60 }}>Δ</TableCell>
                <TableCell align="center" sx={{ ...headerSx, minWidth: 60 }}>Type</TableCell>
                <TableCell align="right" sx={{ ...headerSx, minWidth: 60 }}>Δ%</TableCell>
                <TableCell sx={{ ...headerSx, minWidth: 80 }}>Raison</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3, color: '#888' }}>
                    Aucun changement récent
                  </TableCell>
                </TableRow>
              ) : history.map((row, idx) => (
                <TableRow key={row.id || idx} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #e0e0e0' }}>
                  <TableCell sx={cellSx}>
                    <Tooltip title={new Date(row.created_at).toLocaleString('fr-FR')} arrow>
                      <span>{new Date(row.created_at).toLocaleDateString('fr-FR')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={row.product_name || '-'} arrow>
                      <span style={{ fontWeight: 500 }}>{row.product_name || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={row.marketplace} arrow>
                      <img 
                        src={countryFlags[row.marketplace]} 
                        alt={row.marketplace} 
                        style={{ width: 16, height: 12, borderRadius: 2, border: '1px solid #eee' }} 
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 500 }}>{formatPrice(row.old_price, row.marketplace)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: theme.palette.mode === 'dark' ? '#fff' : '#1976d2', fontSize: '1.08em' }}>{formatPrice(row.new_price, row.marketplace)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: row.price_variation > 0 ? theme.palette.success.main : row.price_variation < 0 ? theme.palette.error.main : '#222', fontSize: '1.08em' }}>{formatVariation(row.price_variation)}</TableCell>
                  <TableCell align="center" sx={cellSx}>{getTypeIcon(row.variation_type)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, color: row.variation_percentage > 0 ? theme.palette.success.main : row.variation_percentage < 0 ? theme.palette.error.main : '#222' }}>{formatPercent(row.variation_percentage)}</TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={row.reason || '-'} arrow>
                      <span style={{ maxWidth: 90, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reason || '-'}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default RepricingHistoryCard; 